import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { fetchIndividualClientFileReport } from "../../../store/slices/reportSlice";
import { fetchPatients } from "../../../store/slices/patientsSlice";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import MainLayout from "../../../components/layout/MainLayout";

const STORAGE_URL = "https://trh-backend.clouxiplexi.com/storage/";
const sigSrc = (v) => (!v ? null : v.startsWith("data:") ? v : `${STORAGE_URL}${v}`);
const fmt = (v) => (v ? new Date(v).toLocaleDateString("en-GB") : "—");
const val = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const selectStyles = {
  control: (base) => ({ ...base, minHeight: "36px", fontSize: "0.85rem", borderRadius: "0.375rem", borderColor: "#d9dee3" }),
  menu: (base) => ({ ...base, fontSize: "0.85rem", zIndex: 9999 }),
  option: (base, state) => ({ ...base, backgroundColor: state.isSelected ? "#666ee8" : state.isFocused ? "#f5f5f9" : "white", color: state.isSelected ? "white" : "#566a7f", cursor: "pointer" }),
};

// A single tick item (green check if on)
const Tick = ({ on, children }) => (
  <div className="d-flex align-items-start gap-2 py-1">
    <i className={`ri-checkbox-${on ? "circle-fill text-success" : "blank-circle-line text-muted"}`} style={{ fontSize: "1.05rem", lineHeight: 1.3 }}></i>
    <span className={on ? "" : "text-muted"} style={{ fontSize: "0.85rem" }}>{children}</span>
  </div>
);

const Field = ({ label, children }) => (
  <div className="col-md-4 mb-2">
    <div className="text-muted small">{label}</div>
    <div className="fw-semibold">{children}</div>
  </div>
);

// Accordion section wrapper
const Section = ({ id, num, title, source, open, children }) => (
  <div className="accordion-item border mb-2 rounded overflow-hidden">
    <h2 className="accordion-header">
      <button className={`accordion-button ${open ? "" : "collapsed"} fw-semibold`} type="button" data-bs-toggle="collapse" data-bs-target={`#icfr${id}`}>
        <span className="badge bg-primary me-2">{num}</span>
        {title}
        {source && <span className="badge bg-label-info ms-2 fw-normal" style={{ fontSize: "0.7rem" }}>{source}</span>}
      </button>
    </h2>
    <div id={`icfr${id}`} className={`accordion-collapse collapse ${open ? "show" : ""}`}>
      <div className="accordion-body">{children}</div>
    </div>
  </div>
);

const SubHead = ({ children }) => (
  <div className="fw-bold text-primary border-bottom pb-1 mb-2 mt-2" style={{ fontSize: "0.9rem" }}>{children}</div>
);

const TRANSITION_ACTS = [
  ["act1", "Supervised Outings (community, shopping, recreation)"],
  ["act2", "Family Visits (on-site / therapeutic)"],
  ["act3", "Home Visits (client returns home temporarily)"],
  ["act4", "Overnight / Extended Home Leave"],
  ["act5", "Vocational / Educational Site Visit"],
];

const IndividualClientFileReport = () => {
  const dispatch = useDispatch();
  const { individualClientFileReport: report, isLoading, error } = useSelector((s) => s.reports);
  const { patients } = useSelector((s) => s.patients);

  const [patientId, setPatientId] = useState("");

  useEffect(() => { dispatch(fetchPatients()); }, [dispatch]);
  useEffect(() => { if (patientId) dispatch(fetchIndividualClientFileReport({ patientId })); }, [dispatch, patientId]);

  const patientOptions = (patients || []).map((p) => ({ value: p.id, label: `${p.first_name || ""} ${p.last_name || ""} (${p.mr_no || "N/A"})`.trim() }));

  const d = report?.icf || {};
  const patient = report?.patient || {};
  const progressNotes = report?.progressNotes || [];
  const familyMeetings = report?.familyMeetings || [];
  const transition = report?.transition;
  const caseMgmt = report?.caseManagement;
  const leapConsent = report?.leapConsent;
  const sessions = report?.sessions || {};
  const sources = report?.sources || {};

  // Prefer transition form's discharge/reintegration data, fall back to ICF
  const disch = transition || d;
  const goals = d.goals || [];
  const notes = progressNotes.length ? progressNotes : (d.progress_notes || []);

  const signRows = [
    ["Client", d.client_signature, d.client_sign_date],
    ["Family Representative", d.family_rep_signature, d.family_rep_sign_date],
    ["Consultant Psychiatrist", d.psychiatrist_signature, d.psychiatrist_sign_date],
    ["PRP", d.prp_signature, d.prp_sign_date],
    ["Case Manager", d.case_manager_signature, d.case_manager_sign_date],
    ["LEAP Member (if applicable)", d.leap_member_signature, d.leap_member_sign_date],
  ];

  const sourceBadges = [
    ["Individual Client File", sources.individual_client_file],
    ["Progress Notes (PRP)", sources.progress_notes_prp],
    ["Family Meetings", sources.family_meetings],
    ["Transition Form", sources.transition_form],
    ["Case Management", sources.case_management],
    ["LEAP Consent", sources.leap_consent],
    ["Rehab Plan (2)", sources.individual_rehab_plan_two],
    ["Sessions", sources.sessions],
  ];

  return (
    <MainLayout>
      <Breadcrumbs />

      {/* Header */}
      <div className="card shadow-sm mb-3">
        <div className="card-body" style={{ background: "linear-gradient(135deg, #f5f7ff 0%, #eef2ff 100%)" }}>
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h4 className="mb-1 text-dark fw-bold">Individual Client File Report</h4>
              <div className="text-muted small">Comprehensive Psychiatric Rehabilitation Framework (CARF-Aligned)</div>
              <div className="text-muted" style={{ fontSize: "0.78rem" }}>Recovery-Oriented · Collaborative · Stage-Based · Evidence-Informed · LEAP-Integrated</div>
            </div>
            <div style={{ minWidth: "280px" }}>
              <label className="form-label small fw-bold">Select Patient</label>
              <Select
                options={patientOptions}
                styles={selectStyles}
                value={patientOptions.find((o) => o.value === patientId) || null}
                onChange={(o) => setPatientId(o ? o.value : "")}
                placeholder="Search patient..."
                isSearchable
                isClearable
              />
            </div>
          </div>
          <div className="mt-2 border-top pt-2 text-muted" style={{ fontSize: "0.76rem" }}>
            <strong>Role Definitions:</strong> PRP = Psychiatric Rehabilitation Practitioner (direct rehabilitation services) | Case Manager = Care coordinator (service linkage, monitoring, planning)
          </div>
        </div>
      </div>

      {!patientId ? (
        <div className="card shadow-sm"><div className="card-body text-center text-muted py-5">
          <i className="ri-user-search-line fs-1 d-block mb-2"></i>
          Please select a patient to generate the Individual Client File Report.
        </div></div>
      ) : isLoading ? (
        <div className="card shadow-sm"><div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
        </div></div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : report ? (
        <>
          {/* Data source summary */}
          {/* <div className="card shadow-sm mb-3">
            <div className="card-body py-2 d-flex flex-wrap gap-2 align-items-center">
              <span className="small fw-bold me-1">Data sources:</span>
              {sourceBadges.map(([label, present]) => (
                <span key={label} className={`badge ${present ? "bg-label-success" : "bg-label-secondary"}`}>
                  <i className={`ri-${present ? "check" : "close"}-line me-1`}></i>{label}
                </span>
              ))}
            </div>
          </div> */}

          <div className="accordion" id="icfReportAccordion">
            {/* 1. Client Identification & Intake */}
            <Section id="1" num="1" title="Client Identification & Intake" source="Basic demographic" open>
              <div className="row">
                <Field label="Client Name">{val(d.client_name || patient.name)}</Field>
                <Field label="Client ID / File No">{val(d.client_file_no)}</Field>
                <Field label="Date of Birth">{fmt(d.date_of_birth || patient.dob)}</Field>
                <Field label="Age">{val(d.age || patient.age)}</Field>
                <Field label="Gender">{val(d.gender || patient.gender)}</Field>
                <Field label="Diagnosis (if applicable)">{val(d.diagnosis)}</Field>
                <Field label="Date of Enrollment">{fmt(d.date_of_enrollment)}</Field>
                <Field label="Consultant Psychiatrist">{val(d.consultant_psychiatrist)}</Field>
                <Field label="Consultant Designation">{val(d.consultant_designation)}</Field>
                <Field label="Case Manager Assigned">{val(d.case_manager_assigned)}</Field>
                <Field label="PRP Assigned">{val(d.prp_assigned)}</Field>
                <Field label="Family Representative">{val(d.family_representative)}</Field>
              </div>
              <Tick on={d.consent_collaborative_care}>Consent for Collaborative Care Obtained</Tick>
            </Section>

            {/* 2. Initial Multidimensional Assessment */}
            <Section id="2" num="2" title="Initial Multidimensional Assessment (Baseline)" source="Integrated form · Recovery plan">
              <SubHead>Clinical & Functional</SubHead>
              <div className="row"><div className="col-md-6"><Tick on={d.assess_mental_health_status}>Mental Health Status</Tick><Tick on={d.assess_cognitive_functioning}>Cognitive Functioning</Tick></div>
                <div className="col-md-6"><Tick on={d.assess_insight_motivation}>Insight & Motivation Level</Tick><Tick on={d.assess_risk_assessment}>Risk Assessment</Tick></div></div>
              <SubHead>Life Skills Domains</SubHead>
              <div className="row"><div className="col-md-6"><Tick on={d.assess_living_skills}>Living Skills (ADLs, hygiene)</Tick><Tick on={d.assess_learning_skills}>Learning Skills</Tick></div>
                <div className="col-md-6"><Tick on={d.assess_working_skills}>Working Skills</Tick><Tick on={d.assess_social_skills}>Social Skills</Tick></div></div>
              <SubHead>Recovery Capital</SubHead>
              <div className="row"><div className="col-md-6"><Tick on={d.assess_family_support}>Family Support</Tick><Tick on={d.assess_financial_stability}>Financial Stability</Tick></div>
                <div className="col-md-6"><Tick on={d.assess_housing}>Housing</Tick><Tick on={d.assess_community_integration}>Community Integration</Tick></div></div>
              <SubHead>Assessment Summary / Clinician Notes</SubHead>
              <div className="border rounded p-2 bg-light small" style={{ whiteSpace: "pre-wrap", minHeight: 50 }}>{val(d.assessment_summary)}</div>
            </Section>

            {/* 3. Collaborative Goal Setting */}
            <Section id="3" num="3" title="Collaborative Goal Setting" source="Individual recovery plan">
              {goals.length === 0 ? <div className="text-muted small">No goals recorded.</div> : goals.map((g, i) => (
                <div className="border rounded p-2 mb-2" key={g.id || i}>
                  <div className="fw-semibold text-primary">Goal {i + 1}</div>
                  <div className="mb-1"><span className="text-muted small">I intend to… </span>{val(g.intend_to)}</div>
                  <div className="d-flex mb-1 flex-wrap gap-3 small">
                    <span><strong>Priority:</strong> {val(g.priority_rank)}</span>
                    <span><strong>Target Date:</strong> {fmt(g.target_date)}</span>
                    <span><strong>Achievement:</strong> {val(g.goal_achievement)}%</span>
                    <span><strong>SMART:</strong> {g.smart ? "Yes" : "No"}</span>
                  </div>
                </div>
              ))}
            </Section>

            {/* 4. IRP */}
            <Section id="4" num="4" title="Individualized Rehabilitation Plan (IRP)" source="Individual recovery plan">
              <SubHead>Program Selection</SubHead>
              <div className="row"><div className="col-md-4"><Tick on={d.irp_residential}>Residential</Tick></div>
                <div className="col-md-4"><Tick on={d.irp_day_program}>Day Program</Tick></div>
                <div className="col-md-4"><Tick on={d.irp_home_based}>Home-Based / Outreach</Tick></div></div>
              <SubHead>Program Customization</SubHead>
              <div className="row"><div className="col-md-4"><Tick on={d.irp_living_skills}>Living Skills</Tick><Tick on={d.irp_learning_skills}>Learning Skills</Tick></div>
                <div className="col-md-4"><Tick on={d.irp_working_skills}>Working Skills</Tick><Tick on={d.irp_social_skills}>Social Skills</Tick></div>
                <div className="col-md-4"><Tick on={d.irp_session_plan_codeveloped}>Session Plan Co-developed</Tick><Tick on={d.irp_frequency_duration_defined}>Frequency & Duration Defined</Tick></div></div>
              <div className="row mt-2">
                <Field label="Session Frequency">{val(d.session_frequency)}</Field>
                <Field label="Session Duration">{val(d.session_duration)}</Field>
                <Field label="Program Start Date">{fmt(d.program_start_date)}</Field>
              </div>
            </Section>

            {/* 5. Evidence-Based Interventions */}
            <Section id="5" num="5" title="Evidence-Based Interventions" source="Sessions list · Evidence-based session">
              <SubHead>Core Interventions</SubHead>
              <div className="row"><div className="col-md-4"><Tick on={d.int_psychoeducation}>Psychoeducation</Tick><Tick on={d.int_cbt}>CBT-Based Interventions</Tick><Tick on={d.int_social_skills_training}>Social Skills Training</Tick></div>
                <div className="col-md-4"><Tick on={d.int_occupational_therapy}>Occupational Therapy</Tick><Tick on={d.int_behavioral_activation}>Behavioral Activation</Tick></div>
                <div className="col-md-4"><Tick on={d.int_family_therapy}>Family Therapy</Tick><Tick on={d.int_vocational_training}>Vocational Training</Tick></div></div>
              <SubHead>Specialist Evidence-Based Programs <span className="badge bg-label-info fw-normal">from sessions</span></SubHead>
              <Tick on={d.int_imr || (sessions.imr > 0)}>Illness Management & Recovery (IMR){sessions.imr > 0 ? ` — ${sessions.imr} session(s) attended` : ""}</Tick>
              <Tick on={sessions.familyPsychoeducation > 0}>Family Psychoeducation Session{sessions.familyPsychoeducation > 0 ? ` — ${sessions.familyPsychoeducation} session(s) attended` : ""}</Tick>
              <SubHead>Intervention Notes / Rationale</SubHead>
              <div className="border rounded p-2 bg-light small" style={{ whiteSpace: "pre-wrap", minHeight: 40 }}>{val(d.intervention_notes)}</div>
            </Section>

            {/* 6. Progress Monitoring */}
            <Section id="6" num="6" title="Progress Monitoring — Twice Weekly Notes" source="Progress Notes PRP">
              {notes.length === 0 ? <div className="text-muted small">No progress notes recorded.</div> : (
                <div className="table-responsive">
                  <table className="table table-bordered table-sm align-middle">
                    <thead className="table-light"><tr><th>#</th><th>Date</th><th>Symptoms</th><th>Functional Progress</th><th>Goal %</th><th>Participation</th><th>Barriers</th><th>Adjustments / Plan</th></tr></thead>
                    <tbody>
                      {notes.map((n, i) => (
                        <tr key={i}><td>{i + 1}</td><td>{fmt(n.note_date)}</td><td className="small">{val(n.symptoms)}</td><td className="small">{val(n.functional_progress)}</td><td>{val(n.goal_achievement)}</td><td className="small">{val(n.participation)}</td><td className="small">{val(n.barriers)}</td><td className="small">{val(n.adjustments_plan)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>

            {/* 7. Structured Goal Review Cycle + Family Meetings */}
            <Section id="7" num="7" title="Structured Goal Review Cycle" source="Recovery plan · Family meeting">
              <div className="table-responsive mb-3">
                <table className="table table-bordered table-sm align-middle">
                  <thead className="table-light"><tr><th>Timeline</th><th>Action</th><th>Outcome / Notes</th></tr></thead>
                  <tbody>
                    <tr><td>Weekly</td><td>Progress Check</td><td>{val(d.review_weekly_outcome)}</td></tr>
                    <tr><td>Monthly</td><td>Evaluation</td><td>{val(d.review_monthly_outcome)}</td></tr>
                    <tr><td>Quarterly</td><td>Plan Revision</td><td>{val(d.review_quarterly_outcome)}</td></tr>
                    <tr><td>6-Monthly</td><td>Outcome Decision</td><td>{val(d.review_6monthly_outcome)}</td></tr>
                  </tbody>
                </table>
              </div>
              <SubHead>Monthly Family Meetings</SubHead>
              {familyMeetings.length === 0 ? <div className="text-muted small">No family meetings recorded.</div> : (
                <div className="table-responsive">
                  <table className="table table-bordered table-sm align-middle">
                    <thead className="table-light"><tr><th>Meeting Date</th><th>Attendees</th><th>Key Concerns / Updates</th><th>Action Points</th></tr></thead>
                    <tbody>{familyMeetings.map((m, i) => (<tr key={i}><td>{fmt(m.meeting_date)}</td><td className="small">{val(m.attendees)}</td><td className="small">{val(m.key_concerns)}</td><td className="small">{val(m.action_points)}</td></tr>))}</tbody>
                  </table>
                </div>
              )}
            </Section>

            {/* 8. Stage-Based Rehabilitation Model */}
            <Section id="8" num="8" title="Stage-Based Rehabilitation Model" source="Individual recovery plan">
              <div className="text-muted small mb-2">Minimum 60% Goal Achievement required for stage advancement.</div>
              <div className="table-responsive">
                <table className="table table-bordered table-sm align-middle">
                  <thead className="table-light"><tr><th>Stage</th><th>Role</th><th>Activities</th><th>Date Achieved</th></tr></thead>
                  <tbody>
                    <tr><td>Stage 1</td><td>Client (Skill Building)</td><td className="small">Structured program participation, core life skills</td><td>{fmt(d.stage1_date_achieved)}</td></tr>
                    <tr><td>Stage 2</td><td>Observer</td><td className="small">Observes sessions, builds insight, reduces stigma</td><td>{fmt(d.stage2_date_achieved)}</td></tr>
                    <tr><td>Stage 3</td><td>Co-Facilitator</td><td className="small">Assists sessions, applies skills</td><td>{fmt(d.stage3_date_achieved)}</td></tr>
                    <tr><td>Stage 4</td><td>Facilitator</td><td className="small">Leads sessions independently under PRP supervision</td><td>{fmt(d.stage4_date_achieved)}</td></tr>
                  </tbody>
                </table>
              </div>
              <Field label="Duration in Program">{val(d.duration_in_program)}</Field>
            </Section>

            {/* 9. Transition & Reintegration */}
            <Section id="9" num="9" title="Transition & Reintegration Process" source="Transition form">
              <SubHead>9A. Graduated Transition Activities</SubHead>
              <div className="table-responsive mb-3">
                <table className="table table-bordered table-sm align-middle">
                  <thead className="table-light"><tr><th>Activity</th><th>Initiated Date</th><th>Frequency</th><th>Accompanied By</th><th>Status</th></tr></thead>
                  <tbody>
                    {transition ? TRANSITION_ACTS.map(([k, label]) => (
                      <tr key={k}><td className="small">{label}</td><td>{fmt(transition[`${k}_initiated_date`])}</td><td>{val(transition[`${k}_frequency`])}</td><td>{val(transition[`${k}_accompanied_by`])}</td><td>{val(transition[`${k}_status`])}</td></tr>
                    )) : (d.transition_activities || []).map((a, i) => (
                      <tr key={i}><td className="small">{val(a.activity)}</td><td>{fmt(a.initiated_date)}</td><td>{val(a.frequency)}</td><td>{val(a.accompanied_by)}</td><td>{val(a.activity_status)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <SubHead>9B. Discharge Preparation</SubHead>
              <div className="row"><div className="col-md-6">
                <Tick on={disch.disch_criteria_reviewed}>Discharge criteria reviewed with client and family</Tick>
                <Tick on={disch.disch_relapse_prevention}>Relapse prevention plan completed (WRAP or equivalent)</Tick>
                <Tick on={disch.disch_community_support}>Community support services identified and linked</Tick>
                <Tick on={disch.disch_medication_plan}>Medication management plan confirmed</Tick>
              </div><div className="col-md-6">
                  <Tick on={disch.disch_crisis_contacts}>Crisis contacts established</Tick>
                  <Tick on={disch.disch_followup_appointments}>Outpatient / follow-up appointments scheduled</Tick>
                  <Tick on={disch.disch_housing_confirmed}>Housing confirmed and safety-assessed</Tick>
                  <Tick on={disch.disch_family_orientation}>Family orientation and briefing conducted</Tick>
                </div></div>
              <div className="row mt-2">
                <Field label="Planned Discharge Date">{fmt(disch.planned_discharge_date)}</Field>
                <Field label="Reintegration Date">{fmt(disch.reintegration_date)}</Field>
              </div>
              <SubHead>Reintegration Notes</SubHead>
              <div className="border rounded p-2 bg-light small" style={{ whiteSpace: "pre-wrap", minHeight: 40 }}>{val(disch.reintegration_notes)}</div>
            </Section>

            {/* 10. Post-Reintegration Follow-Up */}
            <Section id="10" num="10" title="Post-Reintegration Follow-Up" source="Case management">
              <div className="row"><div className="col-md-6">
                <Tick on={d.follow_case_manager || !!caseMgmt}>Case Manager Assigned{caseMgmt?.case_manager ? ` — ${caseMgmt.case_manager}` : ""}</Tick>
                <Tick on={d.follow_monitoring_plan || !!caseMgmt?.review_frequency}>Monitoring Plan{caseMgmt?.review_frequency ? ` — ${caseMgmt.review_frequency}` : ""}</Tick>
                <Tick on={d.follow_relapse_prevention}>Relapse Prevention</Tick>
              </div><div className="col-md-6">
                  <Tick on={d.follow_family_engagement}>Family Engagement</Tick>
                  <Tick on={d.follow_employment_tracking}>Employment Tracking</Tick>
                </div></div>
              <SubHead>Follow-Up Notes</SubHead>
              <div className="border rounded p-2 bg-light small" style={{ whiteSpace: "pre-wrap", minHeight: 40 }}>{val(d.followup_notes || caseMgmt?.remarks)}</div>
            </Section>

            {/* 11. LEAP Domain */}
            <Section id="11" num="11" title="LEAP Domain (Lived Experience Advisory Panel)" source="LEAP form">
              <SubHead>Eligibility</SubHead>
              <div className="row"><div className="col-md-6"><Tick on={d.leap_goal_achievement}>≥ 60–80% Goal Achievement</Tick><Tick on={d.leap_completed_facilitator}>Completed Facilitator Stage</Tick></div>
                <div className="col-md-6"><Tick on={d.leap_demonstrates_insight}>Demonstrates Insight, Stability, Stigma-Free Attitude, Emotional Regulation</Tick><Tick on={d.leap_approved}>Approved by PRP, Case Manager, and Family</Tick></div></div>
              <SubHead>LEAP Role Assigned</SubHead>
              <div className="row"><div className="col-md-4"><Tick on={d.leap_role_peer_support}>Peer Support Facilitator</Tick></div><div className="col-md-4"><Tick on={d.leap_role_facilitator}>Facilitator / Co-Facilitator</Tick></div><div className="col-md-4"><Tick on={d.leap_role_advisory}>Advisory Panel Member</Tick></div></div>
              <SubHead>Training Completed</SubHead>
              <div className="row"><div className="col-md-6"><Tick on={d.leap_train_facilitation}>Facilitation Skills</Tick><Tick on={d.leap_train_communication}>Communication & Boundaries</Tick><Tick on={d.leap_train_ethics}>Ethics & Confidentiality</Tick></div>
                <div className="col-md-6"><Tick on={d.leap_train_mhfa}>Mental Health First Aid</Tick><Tick on={d.leap_train_selfcare}>Self-Care & Relapse Prevention</Tick></div></div>
              <SubHead>Participation Structure</SubHead>
              <div className="row"><div className="col-md-6"><Tick on={d.leap_supervisor_assigned}>Supervisor Assigned</Tick><Tick on={d.leap_role_defined}>Role Defined</Tick></div>
                <div className="col-md-6"><Tick on={d.leap_engagement_plan}>Engagement Plan</Tick><Tick on={d.leap_supervision_scheduled}>Supervision Sessions Scheduled</Tick></div></div>
              <SubHead>Monitoring</SubHead>
              <div className="table-responsive"><table className="table table-bordered table-sm align-middle"><thead className="table-light"><tr><th>Timeline</th><th>Focus</th><th>Notes</th></tr></thead><tbody>
                <tr><td>Monthly</td><td>Role Performance</td><td>{val(d.leap_monitor_monthly)}</td></tr>
                <tr><td>Quarterly</td><td>Leadership Growth</td><td>{val(d.leap_monitor_quarterly)}</td></tr>
                <tr><td>6-Monthly</td><td>Continuation Decision</td><td>{val(d.leap_monitor_6monthly)}</td></tr>
              </tbody></table></div>
              <SubHead>LEAP Outcomes</SubHead>
              <div className="row"><div className="col-md-6"><Tick on={d.leap_out_continued_leadership}>Continued Leadership</Tick><Tick on={d.leap_out_paid_volunteer}>Paid / Volunteer Roles</Tick></div>
                <div className="col-md-6"><Tick on={d.leap_out_workforce}>Workforce Integration</Tick><Tick on={d.leap_out_advocacy}>Advocacy Roles</Tick></div></div>
              <SubHead>Risk & Safeguards</SubHead>
              <div className="row"><div className="col-md-6"><Tick on={d.leap_risk_burnout}>Burnout Monitoring</Tick><Tick on={d.leap_risk_emotional_support}>Emotional Support</Tick></div>
                <div className="col-md-6"><Tick on={d.leap_risk_boundaries}>Boundaries Enforcement</Tick><Tick on={d.leap_risk_crisis_protocol}>Crisis Protocol</Tick></div></div>
              {leapConsent && (
                <div className="alert alert-light border mt-2 mb-0 small">
                  <strong>LEAP Participation Consent:</strong> {leapConsent.agreement_confirmed ? "Agreed ✓" : "Not confirmed"}
                  {leapConsent.participant_name ? ` · Participant: ${leapConsent.participant_name}` : ""}
                  {leapConsent.participant_sign_date ? ` · ${fmt(leapConsent.participant_sign_date)}` : ""}
                </div>
              )}
            </Section>

            {/* 12. Documentation & Quality */}
            <Section id="12" num="12" title="Documentation & Quality (CARF Compliance)" source="Individual Client File">
              <div className="row"><div className="col-md-6">
                <Tick on={d.carf_person_centered}>Person-Centered Documentation</Tick>
                <Tick on={d.carf_cultural_sensitivity}>Cultural Sensitivity</Tick>
                <Tick on={d.carf_ethical_practice}>Ethical Practice</Tick>
              </div><div className="col-md-6">
                  <Tick on={d.carf_measurable_outcomes}>Measurable Outcomes</Tick>
                  <Tick on={d.carf_quality_improvement}>Continuous Quality Improvement</Tick>
                  <Tick on={d.carf_client_family_feedback}>Client & Family Feedback</Tick>
                </div></div>
            </Section>

            {/* 13. Program Duration */}
            <Section id="13" num="13" title="Program Duration (Individualized)" source="IRP">
              <SubHead>Duration informed by</SubHead>
              <div className="row"><div className="col-md-4"><Tick on={d.dur_strengths}>Strengths</Tick><Tick on={d.dur_motivation}>Motivation</Tick></div>
                <div className="col-md-4"><Tick on={d.dur_social_support}>Social Support</Tick><Tick on={d.dur_financial_capacity}>Financial Capacity</Tick></div>
                <div className="col-md-4"><Tick on={d.dur_occupational_readiness}>Occupational Readiness</Tick><Tick on={d.dur_recovery_capital}>Recovery Capital</Tick></div></div>
              <div className="row mt-2">
                <Field label="Estimated Duration">{val(d.estimated_duration)}</Field>
                <Field label="Actual End Date">{fmt(d.actual_end_date)}</Field>
              </div>
            </Section>
          </div>

          {/* Final Sign-Off */}
          <div className="card shadow-sm mt-3">
            <div className="card-header fw-bold">Final Collaborative Sign-Off</div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered align-middle">
                  <thead className="table-light"><tr><th style={{ width: "28%" }}>Role</th><th>Signature</th><th style={{ width: "22%" }}>Date</th></tr></thead>
                  <tbody>
                    {signRows.map(([role, sig, date]) => (
                      <tr key={role} style={{ height: 44 }}>
                        <td>{role}</td>
                        <td>{sigSrc(sig) ? <img src={sigSrc(sig)} alt={`${role} signature`} style={{ height: 38, maxWidth: 200, objectFit: "contain" }} /> : <span className="text-muted">—</span>}</td>
                        <td>{fmt(date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-center text-muted small mt-2">
                ✔ CARF-Compliant · ✔ Recovery-Oriented · ✔ Family & Client Co-Ownership · ✔ LEAP-Integrated · ✔ Evidence-Based
              </div>
              <div className="text-center text-muted" style={{ fontSize: "0.72rem" }}>CONFIDENTIAL — For Authorised Clinical Use Only</div>
            </div>
          </div>
        </>
      ) : null}
    </MainLayout>
  );
};

export default IndividualClientFileReport;
